import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GameShell, shuffle } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const LINE_PREVIEW_MS = 3000;
const LINE_TRANSITION_MS = 180;
const { normalizeBrainGameLevel } = brainGameLevels;

const normalizeAngle = (angle) => ((angle % 180) + 180) % 180;

const createLineRound = (level = 1) => {
    const targetAngle = Math.floor(Math.random() * 180);
    const delta = Math.max(3, 24 - Math.floor((level - 1) * 1.25));
    const foilOffsets = shuffle([-2, -1, 1, 2]).slice(0, 3);
    const correctId = `line-${targetAngle}`;

    return {
        correctId,
        id: `${Date.now()}-${Math.random()}`,
        options: shuffle([
            { angle: targetAngle, id: correctId },
            ...foilOffsets.map((offset, index) => {
                const angle = normalizeAngle(targetAngle + delta * offset);
                return { angle, id: `line-${angle}-foil-${index}` };
            }),
        ]),
        targetAngle,
    };
};

function LineStimulus({ angle }) {
    return (
        <span className="UFOV-brain-games__line-stimulus" aria-hidden="true">
            <i style={{ transform: `rotate(${angle}deg)` }} />
        </span>
    );
}

function LineJudgeGame({ copy = {}, initialLevel, ...props }) {
    const startingLevel = normalizeBrainGameLevel('line-judge', initialLevel);
    const timersRef = useRef([]);
    const [round, setRound] = useState(() => createLineRound(startingLevel));
    const [phase, setPhase] = useState('preview');
    const [previewSeconds, setPreviewSeconds] = useState(3);
    const [choice, setChoice] = useState('');
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const correct = choice && choice === round.correctId;

    const schedulePreview = useCallback(() => {
        timersRef.current.push(window.setTimeout(() => setPreviewSeconds(2), 1000));
        timersRef.current.push(window.setTimeout(() => setPreviewSeconds(1), 2000));
        timersRef.current.push(window.setTimeout(() => {
            setPhase('transition');
            timersRef.current.push(window.setTimeout(() => setPhase('answer'), LINE_TRANSITION_MS));
        }, LINE_PREVIEW_MS));
    }, []);

    const beginRound = useCallback((nextStreak = 0) => {
        const nextLevel = normalizeBrainGameLevel('line-judge', startingLevel + nextStreak);
        const nextRound = createLineRound(nextLevel);
        setRound(nextRound);
        setChoice('');
        setPhase('preview');
        setPreviewSeconds(3);
        schedulePreview();
    }, [schedulePreview]);

    useEffect(() => {
        schedulePreview();
        return () => timersRef.current.forEach((timer) => window.clearTimeout(timer));
    }, [schedulePreview]);

    const choose = (optionId) => {
        if (phase !== 'answer' || choice) {
            return;
        }

        const isCorrect = optionId === round.correctId;
        const nextStreak = isCorrect ? streak + 1 : 0;
        setChoice(optionId);
        setScore((current) => current + (isCorrect ? 1 : 0));
        setStreak(nextStreak);
        setPhase('feedback');
        timersRef.current.push(window.setTimeout(() => beginRound(nextStreak), isCorrect ? 700 : 950));
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level: normalizeBrainGameLevel('line-judge', startingLevel + streak), score, streak }}
            tone={correct ? 'good' : choice ? 'miss' : 'ready'}
        >
            <div className={`UFOV-brain-games__line-game is-${phase}`}>
                {phase === 'preview' ? (
                    <div className="UFOV-brain-games__line-preview">
                        <strong className="UFOV-brain-games__line-cue">{copy.linePreviewCue}</strong>
                        <div className="UFOV-brain-games__line-reference">
                            <LineStimulus angle={round.targetAngle} />
                        </div>
                        <span
                            aria-label={formatGameCopy(copy.secondsRemaining, { seconds: previewSeconds })}
                            className="UFOV-brain-games__line-countdown"
                            role="timer"
                        >
                            {previewSeconds}
                        </span>
                    </div>
                ) : phase === 'transition' ? <span className="UFOV-brain-games__line-transition" /> : (
                    <>
                        <strong className="UFOV-brain-games__line-cue">{copy.lineAnswerCue}</strong>
                        <div className="UFOV-brain-games__line-options">
                            {round.options.map((option, index) => (
                                <button
                                    aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                                    className={`${choice === option.id ? 'is-picked' : ''} ${choice && option.id === round.correctId ? 'is-correct' : ''}`.trim()}
                                    disabled={phase !== 'answer'}
                                    key={option.id}
                                    onClick={() => choose(option.id)}
                                    type="button"
                                >
                                    <LineStimulus angle={option.angle} />
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </GameShell>
    );
}

export default LineJudgeGame;
